import Vue from 'vue'
import i18n from '@/i18n/index'
import util from './util'

// 菜单和权限的事实来源在后端（permission_menu 表）。这个文件只管一件事：
//   登录响应里的导航树 —— 当前账号已按权限过滤好的菜单树，没有单独的接口，
//                        登录时存一份到 localStorage，刷新页面还能读到
// 「建角色时能勾哪些菜单」是另一回事，走 menu/permissionTree 这个接口，
// 由角色页自己请求，跟这里的导航树不耦合（子账号的导航树里也推不出完整的
// 可授权范围）。
//
// 后端只下发 menu_key + name，不下发路由，所以路由映射留在前端。新增菜单时
// 后端插一行 permission_menu、前端在 MENU_ROUTES 补一行路由即可。
//
// i18n: 后端的 name 只有一种语言，站点是中英双语，所以菜单名优先用前端文案，
//       认不出来的 key 才退回后端给的 name（见 menuLabel）。
// assignable: 对应 permission_menu.authorizable=0，主账号专属、子账号永远拿不到。
//       两个用处：角色选择器里不出现，以及路由守卫的二次拦截。
// routed: 该页面在当前代码里有没有落地。结算明细本版后端不插入 permission_menu
//       这两行，页面也还没做；下版接上时只改后端数据，前端不用动。
export const MENU_ROUTES = {
  media: { path: '/abroad/app/list', i18n: 'menu.media', top: true },
  application_list: { path: '/abroad/app/list', i18n: 'title.appList' },
  ad_zone_list: { path: '/abroad/ad/list', i18n: 'title.adsenseList' },
  block_manage: { path: '/abroad/block/list', i18n: 'title.blockManage' },
  // 「数据」一级菜单本身就是权限位，没有二级
  data_report: { path: '/abroad/stats/info', i18n: 'menu.data', top: true },
  docking: { path: '/abroad/doc/sdkWord', i18n: 'menu.docking', top: true },
  sdk_doc: { path: '/abroad/doc/sdkWord', i18n: 'subAccount.menu.sdkDoc' },
  sdk_testing: { path: '/abroad/doc/sdkTest', i18n: 'subAccount.menu.sdkTest' },
  app_ads_txt: { path: '/abroad/doc/appadsTxt', i18n: 'subAccount.menu.appAdsTxt' },
  account: { path: '/abroad/account/companyInfo', i18n: 'menu.Account', top: true },
  company_info: { path: '/abroad/account/companyInfo', i18n: 'title.companyInfo', assignable: false },
  bank_info: { path: '/abroad/account/bankInfo', i18n: 'subAccount.menu.bankInfo', assignable: false, routed: false },
  sub_account_manage: { path: '/abroad/account/subAccount', i18n: 'subAccount.menu.subAccount', assignable: false },
  settlement_view: { path: '/abroad/account/settle', i18n: 'subAccount.menu.settleView', routed: false },
  settlement_operate: { path: '/abroad/account/settle', i18n: 'subAccount.menu.settleOperate', routed: false }
}

// 没有独立权限位的子页面（/abroad/ad/addAD 之类）按 /abroad/<段>/ 归到它的列表页，
// 否则「添加广告位」会被路由守卫误判成无权限
const SECTION_MAP = {
  app: 'application_list',
  ad: 'ad_zone_list',
  block: 'block_manage',
  stats: 'data_report'
}

// 还没拿到导航树（后端登录响应里还没带、或者本地那份被清了）时，
// 导航按主账号渲染 —— 首屏不闪一下空导航，也不会整个导航消失。
// 这里只排菜单结构，名字和路由还是从 MENU_ROUTES 取
const FALLBACK_TREE = [
  {
    menu_key: 'media',
    children: [{ menu_key: 'application_list' }, { menu_key: 'ad_zone_list' }, { menu_key: 'block_manage' }]
  },
  { menu_key: 'data_report' },
  {
    menu_key: 'docking',
    children: [{ menu_key: 'sdk_doc' }, { menu_key: 'sdk_testing' }, { menu_key: 'app_ads_txt' }]
  },
  {
    menu_key: 'account',
    children: [{ menu_key: 'company_info' }, { menu_key: 'bank_info' }, { menu_key: 'sub_account_manage' }]
  }
]

// 导航树存在 localStorage，不塞进 user 那个 cookie：
// cookie 有 4K 上限而且每个请求都会带上去，另外 login-domestic / login-overseas
// 这类落地页会整个覆写 user cookie，树挂在那儿会被顺手抹掉
const STORAGE_KEY = 'navTree'

const state = Vue.observable({
  loaded: false,
  // 后端下发的导航树原样（[{ menu_key, name, children }]）
  tree: [],
  // tree 摊平出来的 menu_key 集合，判权限用
  keys: [],
  // 后端如果带了 is_main / is_owner 就用它，没带是 null（见 isMain）
  isMainFlag: null
})

// 登录响应里导航树的字段名。后端定下来之后这张候选表可以收成一个
const TREE_FIELDS = ['nav_tree', 'menu_tree', 'menus', 'list', 'tree']

function looksLikeTree(arr) {
  return Array.isArray(arr) && arr.length > 0 && !!nodeKey(arr[0])
}

// 登录响应是一个大对象，list / menus 这种通名有可能是别的东西，
// 所以认字段之前先看一眼第一个元素有没有 menu_key。
// 字段在、但是空数组（子账号一个菜单都没有）也算有效，当兜底候选
function pickTree(res) {
  if (Array.isArray(res)) return looksLikeTree(res) ? res : null
  if (!res) return null
  let empty = null
  for (let i = 0; i < TREE_FIELDS.length; i++) {
    const v = res[TREE_FIELDS[i]]
    if (looksLikeTree(v)) return v
    if (Array.isArray(v) && !v.length && !empty) empty = v
  }
  return empty
}

// 后端 JWT 里这个标记叫 isOwner，响应体上可能写成 is_main 或 is_owner
function pickIsMain(res) {
  if (!res) return null
  if (res.is_main !== undefined) return res.is_main
  if (res.is_owner !== undefined) return res.is_owner
  if (res.isOwner !== undefined) return res.isOwner
  return null
}

// 存下来的树连着当时那个 token 一起记。读的时候 token 对不上就丢掉 ——
// 换了账号登进来（包括 adx 后台带 token 免密进来这种不经过 saveFromLogin 的路子），
// 上一个账号的导航不会被当成这次的
function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.token !== util.getCookie('token')) return null
    return pickTree(parsed) ? parsed : null
  } catch (e) {
    // 存的内容坏了（手改过、或者旧版本留下的格式）：当没存过处理
    return null
  }
}

function nodeKey(node) {
  return (node && (node.menu_key || node.key)) || ''
}

// 后端的树节点用 menu_key，前端 MENU_ROUTES 这边是 key，统一从这里取
export function menuKey(node) {
  return nodeKey(node)
}

function route(key) {
  return MENU_ROUTES[key] || null
}

export function isAssignable(node) {
  const r = route(nodeKey(node))
  return !r || r.assignable !== false
}

// 有没有对应路由：没有的页面不进导航，也不参与「一级菜单是否显示」的判断
export function isRouted(node) {
  const r = route(nodeKey(node))
  return !!r && r.routed !== false
}

export function menuPath(node) {
  const r = route(nodeKey(node))
  return r ? r.path : ''
}

export function menuLabel(node) {
  const r = route(nodeKey(node))
  if (r && r.i18n) return i18n.t(r.i18n)
  // 后端加了前端还不认识的菜单：用后端的 name 顶上，别显示空白
  return (node && node.name) || nodeKey(node)
}

export function findMenu(key) {
  const r = route(key)
  return r ? Object.assign({ key: key }, r) : null
}

// —— 以下都读后端下发的导航树 ——

export function isLoaded() {
  return state.loaded
}

export function navTree() {
  return state.loaded ? state.tree : FALLBACK_TREE
}

// 主账号：后端给了 is_main 就用它，没给就看导航里有没有「子账号管理」——
// 这一项 authorizable=0，只有主账号会拿到
export function isMain() {
  if (!state.loaded) return true
  if (state.isMainFlag !== undefined && state.isMainFlag !== null) return !!state.isMainFlag
  return state.keys.indexOf('sub_account_manage') !== -1
}

export function hasMenu(key) {
  // 导航树还没回来时不敢收菜单，按全放处理，等加载完再由路由守卫拦
  if (!state.loaded) return true
  return state.keys.indexOf(key) !== -1
}

// 一级菜单下有任意一个有权限且有路由的页面，它才出现在顶部导航（PRD 4.3.1）
export function visibleChildren(topKey) {
  const top = navTree().filter(t => nodeKey(t) === topKey)[0]
  if (!top || !top.children || !top.children.length) return []
  return top.children.filter(isRouted).map(c => ({
    key: nodeKey(c),
    label: menuLabel(c),
    path: menuPath(c)
  }))
}

export function hasTopMenu(topKey) {
  const top = navTree().filter(t => nodeKey(t) === topKey)[0]
  if (!top) return false
  if (!top.children || !top.children.length) return isRouted(top)
  return visibleChildren(topKey).length > 0
}

// 顶部导航点进去落在哪个页面：按树的顺序取第一个有权限的二级页面，
// 写死首页会让「只有广告位列表权限」的角色点「媒体」进到没权限的应用列表
export function firstVisiblePath(topKey) {
  const top = navTree().filter(t => nodeKey(t) === topKey)[0]
  if (!top) return ''
  if (!top.children || !top.children.length) return menuPath(top)
  const children = visibleChildren(topKey)
  return children.length ? children[0].path : ''
}

// 顶部导航直接渲染这个列表，顺序跟后端下发的树一致
export function topMenus() {
  return navTree()
    .filter(t => hasTopMenu(nodeKey(t)))
    .map(t => ({ key: nodeKey(t), label: menuLabel(t), path: firstVisiblePath(nodeKey(t)) }))
}

// 这个账号第一个能进的页面，按顶部导航的顺序取。
// 路由守卫拦下越权访问时送到这里：登录后的落地页在好几个地方写死成了应用列表，
// 子账号不一定有应用列表的权限。一个能进的页面都没有时返回 ''
export function firstAccessiblePath() {
  const tops = topMenus()
  for (let i = 0; i < tops.length; i++) {
    if (tops[i].path) return tops[i].path
  }
  return ''
}

// 某个权限位归属的一级菜单，顶部导航点亮当前项用。
// 一级菜单自己就是权限位时（数据）原样返回
export function topKeyOf(key) {
  let hit = key
  navTree().forEach(top => {
    ;(top.children || []).forEach(c => {
      if (nodeKey(c) === key) hit = nodeKey(top)
    })
  })
  return hit
}

// 路由守卫用：路径归属哪个权限位
export function menuKeyByPath(path) {
  const section = String(path).split('/')[2] // /abroad/<section>/...
  if (SECTION_MAP[section]) return SECTION_MAP[section]
  let hit = null
  Object.keys(MENU_ROUTES).forEach(key => {
    if (MENU_ROUTES[key].path !== path) return
    // 一级菜单和它的首个二级共用 path（docking / sdk_doc），命中时取二级 ——
    // 权限位在二级上
    if (!hit || (MENU_ROUTES[hit].top && !MENU_ROUTES[key].top)) hit = key
  })
  return hit
}

// 角色列表里一个角色的菜单权限展示成什么文案。
// 后端返回的是 [{ menu_key, name }]，这里只把 name 换成前端的双语文案
export function roleMenuLabels(role) {
  const menus = (role && role.menus) || []
  return menus.map(menuLabel)
}

export function setNavTree(res) {
  const tree = pickTree(res) || []
  const keys = []
  tree.forEach(top => {
    keys.push(nodeKey(top))
    ;(top.children || []).forEach(c => keys.push(nodeKey(c)))
  })
  state.tree = tree
  state.keys = keys
  state.isMainFlag = pickIsMain(res)
  state.loaded = true
}

export function reset() {
  state.loaded = false
  state.tree = []
  state.keys = []
  state.isMainFlag = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // 无痕模式之类写不了 localStorage：内存里已经清干净了，够用
  }
}

// 登录成功时调一次：导航树没有独立接口，后端从 login 一起返回。
// 存一份到 localStorage，刷新页面不用重新登录也还在
export function saveFromLogin(res) {
  const tree = pickTree(res)
  if (!tree) {
    // 这个响应里没有树（后端还没开始下发，或者压根不是登录响应）：
    // 把上一个账号留下的那份清掉，未加载状态下导航会按主账号兜底渲染，
    // 越权访问还有后端接口那一层兜
    reset()
    return
  }
  // token 一起存下来，下次读的时候要对得上才算数（见 readStored）
  const payload = { token: util.getCookie('token'), list: tree, is_main: pickIsMain(res) }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (e) {
    // 存不下就只在内存里留着，这次会话内仍然有效
  }
  setNavTree(payload)
}

// 导航树来自登录响应，这里只是把 localStorage 里那份读进内存。
// 返回 Promise 是为了调用方（路由守卫、header）的写法不用动：
// 读不到时 reject，调用方各自兜底 —— 未加载时 hasMenu 全放、导航按主账号渲染
export function load() {
  if (state.loaded) return Promise.resolve(state.tree)
  const stored = readStored()
  if (!stored) return Promise.reject(new Error('nav tree unavailable'))
  setNavTree(stored)
  return Promise.resolve(state.tree)
}

export default {
  MENU_ROUTES,
  findMenu,
  firstAccessiblePath,
  firstVisiblePath,
  hasMenu,
  hasTopMenu,
  isAssignable,
  isLoaded,
  isMain,
  isRouted,
  load,
  menuKey,
  menuKeyByPath,
  menuLabel,
  menuPath,
  navTree,
  reset,
  roleMenuLabels,
  saveFromLogin,
  setNavTree,
  topKeyOf,
  topMenus,
  visibleChildren
}
